/**
 * useAutoLocale — auto-detects and sets the user's locale (language + market)
 * based on which Hotmart products they own.
 *
 * Rules:
 *   - EN products (Clean Bakes 7456362/8061842, Clean Sweets 7381203, Clean Meals 7381661) → EN + US
 *   - ES products (Panadería Cero, Repostería Cero, Repostería Premium, Cocina Real) → ES + MX
 *   - DE products (Kuchenglück 7778364, Brotglück 7778710, Kochglück 7778879) → DE + DE
 *   - EN takes priority if user owns products from both markets
 *   - DE takes priority over ES (German buyers likely have a German device)
 *
 * For EN-only users: language is ALWAYS forced to EN, even if previously set to ES.
 * All their content is in English — showing ES is always wrong for them.
 *
 * For DE-only users: language is ALWAYS forced to DE.
 *
 * For mixed users (EN + ES products): respect manual language selection.
 * For first-time users with no selection: detect once and store.
 */
import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import type { SupportedLanguage } from "@/i18n";
import type { SupportedMarket } from "@/contexts/LocaleContext";
import { SUPPORTED_LANGUAGES } from "@/i18n";

// EN-specific product IDs
const EN_PRODUCT_IDS = new Set([
  "7456362",  // Clean Bakes (legacy)
  "8061842",  // Clean Bakes
  "7381203",  // Clean Sweets
  "7381661",  // Clean Meals
]);

// ES-specific product IDs
const ES_PRODUCT_IDS = new Set([
  "7222660", "7418452",  // Panadería Cero ES
  "7368845",             // Repostería Cero
  "7339562",             // Cocina Real (front)
  "7368977",             // Cocina Real (upsell de Repostería Premium)
]);

// DE-specific product IDs (German market)
const DE_PRODUCT_IDS = new Set([
  "7778364",  // Kuchenglück
  "7778710",  // Brotglück
  "7778879",  // Kochglück
]);

function detectLocale(
  productIds: string[]
): { lang: SupportedLanguage; market: SupportedMarket } | null {
  // EN takes priority — if user has any EN product they are an EN user
  if (productIds.some((id) => EN_PRODUCT_IDS.has(id))) {
    return { lang: "en", market: "us" };
  }
  // DE before ES — German products are exclusively for the German market
  if (productIds.some((id) => DE_PRODUCT_IDS.has(id))) {
    return { lang: "de", market: "de" };
  }
  if (productIds.some((id) => ES_PRODUCT_IDS.has(id))) {
    return { lang: "es", market: "mx" };
  }
  return null;
}

// Storage key prefix; bump version to force re-detection for all existing users
const STORAGE_KEY_PREFIX = "locale_auto_detected_v3";

// iOS Safari Private mode / ITP-cleared storage can throw — wrap accordingly
const safeGet = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const safeSet = (key: string, value: string): void => {
  try { localStorage.setItem(key, value); } catch { /* noop */ }
};

// Canonical market for each language (keeps market aligned with language).
const LANGUAGE_MARKET: Record<SupportedLanguage, SupportedMarket> = {
  en: "us",
  es: "mx",
  de: "de",
};

/**
 * useAutoLocale is the SINGLE source of truth for locale resolution.
 * (AuthContext no longer sets the language — it raced with this hook and had
 * no manual-selection guard, which reset the user's chosen language.)
 *
 * Priority — highest wins:
 *   1. Manual selection on this device (app_language_user_selected) — never overridden
 *   2. DB preferred_language (only ever written on a genuine manual pick) — cross-device sync
 *   3. Product-derived language (EN-only / DE-only enforcement, then one-time detection)
 *   4. Device/browser detection (handled at startup by LocaleContext) → default "es"
 */
export const useAutoLocale = () => {
  const { user, loading } = useAuth();
  const { setLocale, language } = useLocale();

  useEffect(() => {
    // Wait until auth is resolved.
    if (loading || !user) return;

    // ── Priority 1: an explicit manual choice on THIS device always wins ──
    if (safeGet("app_language_user_selected") === "true") return;

    // ── Priority 2: DB preferred_language — syncs the explicit choice across
    //    devices and outranks product-derived enforcement. Applies to ALL users
    //    (with or without products); replaces the old AuthContext path. ──
    const dbLang = user.preferred_language;
    if (dbLang && SUPPORTED_LANGUAGES.includes(dbLang as SupportedLanguage)) {
      if (dbLang !== language) {
        setLocale(dbLang as SupportedLanguage, LANGUAGE_MARKET[dbLang as SupportedLanguage] ?? "mx");
      }
      // Pin it so nothing re-detects over it on this device.
      safeSet("app_language_user_selected", "true");
      return;
    }

    // ── Priority 3: product-derived language (needs products loaded) ──
    if (user.products.length === 0) return;

    const hasEnProduct = user.products.some((id) => EN_PRODUCT_IDS.has(id));
    const hasEsProduct = user.products.some((id) => ES_PRODUCT_IDS.has(id));
    const hasDeProduct = user.products.some((id) => DE_PRODUCT_IDS.has(id));
    const isEnOnly = hasEnProduct && !hasEsProduct && !hasDeProduct;
    const isDeOnly = hasDeProduct && !hasEnProduct && !hasEsProduct;
    const detectedKey = `${STORAGE_KEY_PREFIX}_${user.id}`;

    // Single-language markets: enforce whenever the language drifted (device
    // detection, cleared storage, shared device).
    if (isEnOnly && language !== "en") {
      setLocale("en", "us");
      safeSet(detectedKey, "true");
      return;
    }
    if (isDeOnly && language !== "de") {
      setLocale("de", "de");
      safeSet(detectedKey, "true");
      return;
    }

    // ── First-launch detection, once per device ──
    if (safeGet(detectedKey)) return;

    const detected = detectLocale(user.products);
    if (detected) {
      safeSet(detectedKey, "true");
      setLocale(detected.lang, detected.market);
    }
  }, [user?.id, user?.products?.length, user?.preferred_language, loading, setLocale, language]);
};
